/**
 * CollectionOffersList - Display list of active collection offers
 *
 * Shows offers for a collection with:
 * - Buyer address
 * - Offer amount
 * - Seller receives amount
 * - Expiration
 * - Accept button (for NFT owners)
 * - Cancel button (for offer owners)
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import { Table } from '../../ui/components/Table';
import { Badge } from '../../ui/components/Badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import {
  getCollectionOffers,
  cancelCollectionOffer,
  markOfferCancelled,
  type CollectionOffer,
  type CollectionOfferStats,
} from '../../api/collectionOffers';
import { formatDistanceToNow } from 'date-fns';
import { formatPrice } from '../../utils/formatters';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { CancelCollectionOfferModal } from './CancelCollectionOfferModal';

interface CollectionOffersListProps {
  collectionId: string;
  onAcceptOffer?: (offer: CollectionOffer) => void;
  userOwnsNfts?: boolean; // Whether the logged-in user owns any NFTs in this collection
  refreshKey?: number; // Increment to trigger a refresh
}

export const CollectionOffersList: React.FC<CollectionOffersListProps> = ({
  collectionId,
  onAcceptOffer,
  userOwnsNfts = false,
  refreshKey = 0,
}) => {
  const { user, isAuthenticated } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();
  const [offers, setOffers] = useState<CollectionOffer[]>([]);
  const [stats, setStats] = useState<CollectionOfferStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cancel offer state
  const [cancellingOffer, setCancellingOffer] = useState<CollectionOffer | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    // Only load offers for authenticated users
    if (isAuthenticated) {
      loadOffers();
    } else {
      setLoading(false);
    }
  }, [collectionId, refreshKey, isAuthenticated]);

  const loadOffers = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getCollectionOffers(collectionId, {
        limit: 20,
        orderBy: 'amount_desc',
      });
      setOffers(result.offers);
      setStats(result.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address: string): string => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatExpiration = (expiresAt: string | null): string => {
    if (!expiresAt) return 'No expiration';
    const date = new Date(expiresAt);
    if (date < new Date()) return 'Expired';
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // Format trait filters for display - show each trait type on its own line with comma-separated values
  const formatTraitFilters = (traitFilters: Record<string, string[]> | null | undefined): React.ReactNode => {
    if (!traitFilters || Object.keys(traitFilters).length === 0) {
      return <span className="text-subtext-color italic">Any traits</span>;
    }

    const entries = Object.entries(traitFilters);
    return (
      <div className="flex flex-col gap-0.5">
        {entries.map(([traitType, values]) => (
          <div key={traitType} className="text-caption">
            <span className="text-subtext-color">{traitType}:</span>{' '}
            <span className="text-default-font">{values.join(', ')}</span>
          </div>
        ))}
      </div>
    );
  };

  const handleCancelClick = (offer: CollectionOffer) => {
    setCancellingOffer(offer);
    setShowCancelModal(true);
  };

  const handleCancelSuccess = () => {
    setShowCancelModal(false);
    setCancellingOffer(null);
    loadOffers(); // Reload to reflect cancellation
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <p className="text-body text-error">{error}</p>
        <Button variant="neutral-secondary" onClick={loadOffers}>
          Try Again
        </Button>
      </div>
    );
  }

  // Show sign-in prompt for non-authenticated users
  if (!isAuthenticated) {
    return (
      <div className="flex w-full flex-col items-center gap-6 py-8">
        <svg
          width="64"
          height="64"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="32" cy="32" r="31" fill="rgb(7, 48, 59)" stroke="rgb(7, 56, 68)" strokeWidth="2" />
          <path
            d="M32 20c-6.63 0-12 5.37-12 12s5.37 12 12 12 12-5.37 12-12-5.37-12-12-12zm0 22c-5.52 0-10-4.48-10-10s4.48-10 10-10 10 4.48 10 10-4.48 10-10 10zm-1-6h2v2h-2v-2zm0-10h2v8h-2v-8z"
            fill="rgb(5, 162, 194)"
          />
        </svg>
        <span className="text-heading-3 font-heading-3 text-default-font text-center">
          Sign in to view collection offers
        </span>
        <span className="text-body font-body text-subtext-color text-center max-w-md">
          Collection offers are open bids to buy any NFT in this collection. Sign in to view active offers and accept them if you own NFTs in this collection.
        </span>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 w-full">
        <p className="text-heading-3 font-heading-3 text-subtext-color text-center">No collection offers yet</p>
        <p className="text-body text-subtext-color text-center">
          Be the first to make an offer on any NFT in this collection
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Stats Summary */}
      {stats && (
        <div className="flex items-center gap-6 text-body">
          <div>
            <span className="text-subtext-color">Active Offers: </span>
            <span className="text-default-font font-medium">{stats.activeOfferCount}</span>
          </div>
          {stats.bestOfferXrp && (
            <div>
              <span className="text-subtext-color">Best Offer: </span>
              <span className="text-brand-700 font-medium">
                {formatPrice(stats.bestOfferXrp)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Offers Table */}
      <div className="w-full overflow-x-auto">
        <Table className="w-full"
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Buyer</Table.HeaderCell>
              <Table.HeaderCell>Offer Amount</Table.HeaderCell>
              <Table.HeaderCell>You Receive</Table.HeaderCell>
              <Table.HeaderCell>Traits</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              {isAuthenticated && <Table.HeaderCell className="w-24"></Table.HeaderCell>}
            </Table.HeaderRow>
          }
        >
          {offers.map((offer) => {
            const isOwnOffer = user?.xrpl_address === offer.buyerAddress;
            return (
              <Table.Row key={offer.id}>
                <Table.Cell>
                  <Link
                    to={`/wallets/${offer.buyerAddress}`}
                    className="text-brand-700 hover:text-brand-600 underline"
                  >
                    {isOwnOffer ? 'You' : formatAddress(offer.buyerAddress)}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body-bold text-default-font">
                    {formatPrice(offer.displayAmountXrp)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body text-success-600">
                    {formatPrice(offer.sellerReceivesXrp)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {formatTraitFilters(offer.traitFilters)}
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body text-subtext-color">
                    {formatExpiration(offer.expiresAt)}
                  </span>
                </Table.Cell>
                {isAuthenticated && (
                  <Table.Cell>
                    {isOwnOffer ? (
                      <Button
                        variant="destructive-secondary"
                        size="small"
                        onClick={() => handleCancelClick(offer)}
                      >
                        Cancel
                      </Button>
                    ) : userOwnsNfts ? (
                      <Button
                        variant="brand-secondary"
                        size="small"
                        onClick={() => onAcceptOffer?.(offer)}
                      >
                        Accept
                      </Button>
                    ) : null}
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}
        </Table>
      </div>

      {/* Cancel Offer Modal */}
      {cancellingOffer && (
        <CancelCollectionOfferModal
          isOpen={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            setCancellingOffer(null);
          }}
          offer={cancellingOffer}
          onSuccess={handleCancelSuccess}
        />
      )}
    </div>
  );
};
